import { Card, Typography, Tag } from 'antd';
import { MenuSharp, EditOutlined } from '@mui/icons-material';
import StatCard from './StatCard';
import { PrimaryButton } from '../Components/Buttons/Buttons';
import MenuDropdown from './MenuDropdown';
import QRCodeWithHover from './QRCodeWithHover';
import { getCatalogStatus } from './CatalogStatusConfig';
import {
    greyScaleTokens,
    primaryShades,
    variantTokens,
    statusTokens
} from '../../theme/theme';
import styles from './CardCatalog.module.css';

const { Title, Text } = Typography;

// Costanti per i colori del componente usando il sistema di tema
const COLORS = {
    // Card principale
    cardBackground: primaryShades.primary500,
    cardText: variantTokens.white,

    // Overlay e sfondi
    overlayBackground: 'rgba(0, 0, 0, 0.6)',
    statsBackground: greyScaleTokens.grey200,

    // Notifiche e stati
    editingNotificationBg: 'rgba(255, 193, 7, 0.2)',
    editingNotificationBgHover: 'rgba(255, 193, 7, 0.3)',
    editingNotificationText: statusTokens.warning,

    // Testi
    whiteText: variantTokens.white,
    labelText: variantTokens.white,

    // Shadows
    cardShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
    sectionShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    qrShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
};



const CardCatalog = ({ catalogue, getQrCodeUrl, handleDownloadQrCode, cardSectionHeight, handleMenuToggle, activeMenu, setActiveMenu, handleEditDraft, getStatCards, getActionButtons, hasDraftInProgress, canPerformActions = true }) => {
    // Funzione per determinare lo sfondo della card
    const getCardBackground = () => {
        const bannerImage = catalogue.catalogues_page_style?.coverImg;
        const bannerBackgroundColor = catalogue.catalogues_page_style?.bannerBackgroundColor;
        const primaryColor = catalogue.catalogues_page_style?.primaryColor;

        // Se c'è un'immagine del banner, usala
        if (bannerImage) {
            return {
                backgroundImage: `url(${bannerImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            };
        }

        // Se non c'è immagine, controlla il bannerBackgroundColor
        if (bannerBackgroundColor) {
            // Se il colore è bianco o default, usa il primaryColor
            const isWhiteOrDefault = bannerBackgroundColor === '#FFFFFF' ||
                bannerBackgroundColor === '#ffffff' ||
                bannerBackgroundColor === 'white' ||
                bannerBackgroundColor === 'default';

            if (isWhiteOrDefault && primaryColor) {
                return {
                    backgroundColor: primaryColor
                };
            } else {
                return {
                    backgroundColor: bannerBackgroundColor
                };
            }
        }

        // Fallback: usa il primaryColor se disponibile, altrimenti bianco
        return {
            backgroundColor: primaryColor || '#FFFFFF'
        };
    };

    // Nota: Non abbiamo più bisogno di isMobile perché usiamo Container Queries
    return (
        <Card
            key={catalogue.id}
            style={{
                borderRadius: '0.5rem',
                overflow: 'hidden',
                backgroundColor: COLORS.cardBackground,
                color: COLORS.cardText,
                boxShadow: COLORS.cardShadow,
                border: 'none'
            }}
            styles={{
                body: { padding: 0 }
            }}
        >
            {/* Card layout strutturato in due righe di uguale altezza */}
            <div className={`${styles.cardContainer} card-container`} style={{ display: 'flex', flexDirection: 'column', height: catalogue.isDraft ? '12.5rem' : '25rem' }}>
                {/* Top section with background image */}
                <div style={{
                    position: 'relative',
                    ...getCardBackground(),
                    height: catalogue.isDraft ? '100%' : cardSectionHeight
                }}>
                    {/* Overlay nero semi-trasparente */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: COLORS.overlayBackground,
                        zIndex: 1
                    }} />

                    {/* Contenuto principale con layout a 3 colonne */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100%',
                        position: 'relative',
                        zIndex: 2
                    }}>
                        {/* Prima colonna: QR Code */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem',
                            minWidth: '80px',
                            filter: `drop-shadow(${COLORS.qrShadow})`
                        }}>
                            <QRCodeWithHover
                                catalogue={catalogue}
                                getQrCodeUrl={getQrCodeUrl}
                                onDownload={handleDownloadQrCode}
                            />
                        </div>

                        {/* Seconda colonna: Informazioni del catalogo */}
                        <div className={styles.brandInfoSection} style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: '1rem',
                            gap: '1rem'
                        }}>
                            {/* Titolo del catalogo */}
                            <div>
                                <Title level={3} className={styles.cardTitle} style={{
                                    margin: 0,
                                    color: COLORS.whiteText,
                                    lineHeight: 1.2
                                }}>
                                    {catalogue.nameCatalog || '-'}
                                </Title>
                            </div>

                            {/* Informazioni del catalogo */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>SUBSCRIPTION: </Text>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>{catalogue.subscriptionName || '-'}</Text>
                                </div>
                                <div>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>SUBDOMAIN: </Text>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>{catalogue.webDomain || '-'}</Text>
                                </div>
                                <div>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>ACCESS: </Text>
                                    <Text className={styles.cardLabel} style={{ color: COLORS.labelText, fontWeight: 'bold' }}>
                                        <span style={{ textDecoration: 'underline' }}>
                                            {catalogue.isPublic ? 'Public' : 'Private'}
                                        </span>
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {/* Terza colonna: Menu, Notifiche e Tag */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            padding: '1rem',
                            minWidth: '120px'
                        }}>
                            {/* Menu Icon + Dropdown - Solo se l'utente può eseguire azioni */}
                            {canPerformActions && (
                                <div style={{ position: 'relative' }}>
                                    <MenuSharp
                                        className={styles.menuIcon}
                                        style={{
                                            color: COLORS.whiteText,
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s ease',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuToggle(catalogue.id, e);
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.opacity = '0.8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.opacity = '1';
                                        }}
                                    />
                                    <MenuDropdown
                                        catalogueId={catalogue.id}
                                        isVisible={activeMenu === catalogue.id}
                                        onClose={() => setActiveMenu(null)}
                                        onModify={handleEditDraft}
                                    />
                                </div>
                            )}

                            {/* Notification for catalogs in editing mode - Solo se l'utente può eseguire azioni */}
                            {canPerformActions && hasDraftInProgress && (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Call the same function that's used for the Modify action in the menu
                                        if (typeof window.handleModifyPublishedCatalogue === 'function') {
                                            window.handleModifyPublishedCatalogue(catalogue.id);
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: COLORS.editingNotificationBg,
                                        borderRadius: '4px',
                                        padding: '0.25rem 0.5rem',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = COLORS.editingNotificationBgHover;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = COLORS.editingNotificationBg;
                                    }}
                                >
                                    <EditOutlined className={styles.editIcon} style={{ color: COLORS.editingNotificationText, marginRight: '0.25rem' }} />
                                    <Text className={styles.editText} style={{ color: COLORS.editingNotificationText, fontWeight: 'bold' }}>
                                        1 catalog being edited
                                    </Text>
                                </div>
                            )}

                            {/* Tag */}
                            {(() => {
                                const statusConfig = getCatalogStatus(catalogue);
                                return (
                                    <Tag
                                        color={statusConfig.color}
                                        style={{
                                            borderRadius: '0.25rem',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.75rem',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            marginInlineEnd: 0,
                                        }}
                                    >
                                        {statusConfig.label}
                                    </Tag>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* Stats and action buttons section - Only shown for published catalogs */}
                {!catalogue.isDraft && (
                    <div className={styles.statsSection} style={{
                        display: 'flex',
                        backgroundColor: COLORS.statsBackground,
                        height: cardSectionHeight,
                        flexDirection: 'row',
                        boxShadow: COLORS.sectionShadow
                    }}>
                        {/* Stats - Più larghezza per visualizzare meglio i numeri */}
                        <div style={{
                            display: 'flex',
                            height: '100%',
                            alignItems: 'stretch',
                            flexWrap: 'nowrap',
                            justifyContent: 'space-between',
                            width: '75%',
                            padding: '0.5rem',
                            gap: '0.75rem'
                        }}>
                            {getStatCards(catalogue).map((stat, index) => (
                                <StatCard
                                    key={index}
                                    value={stat.value}
                                    label={stat.label}
                                />
                            ))}
                        </div>

                        {/* Action buttons - 25% */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            height: '100%',
                            width: '25%',
                            padding: '0.5rem',
                            gap: '0.4rem'
                        }}>
                            {getActionButtons(catalogue).map((button, index) => (
                                <PrimaryButton
                                    key={index}
                                    icon={button.icon}
                                    onClick={button.onClick}
                                    style={{
                                        width: '8rem'
                                    }}
                                >
                                    {button.label}
                                </PrimaryButton>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default CardCatalog;